import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { c as create$3, F as FormController } from './form.js';
import { a as BusinessFormStep, B as BusinessFormStepV2 } from './business-form-types.js';
import { g as bankNameValidation, n as nicknameValidation, i as identityNameValidation, h as accountTypeValidation, j as accountNumberValidation, k as routingNumberValidation } from './schema-validations.js';
import { c as bankAccountTypeOptions } from './business-form-options.js';
import { A as Api } from './Api.js';
import './Checkout.js';
import './Pagination.js';
import './Payment.js';
import './SubAccount.js';
import { c as config } from './config.js';
import { n as numberOnlyHandler } from './utils5.js';
import { C as ComponentErrorCodes, a as ComponentErrorSeverity } from './ComponentError.js';
import { d as defineCustomElement$5 } from './custom-popper2.js';
import { d as defineCustomElement$4 } from './form-control-error-text2.js';
import { d as defineCustomElement$3 } from './form-control-select2.js';
import { d as defineCustomElement$2 } from './form-control-text2.js';
import { d as defineCustomElement$1 } from './form-control-tooltip2.js';

const businessBankAccountSchema = (allowOptionalFields) => {
    const schema = create$3({
        bank_name: bankNameValidation.required('Enter bank name'),
        nickname: nicknameValidation.required('Enter nickname'),
        account_owner_name: identityNameValidation.required('Enter account owner name'),
        account_type: accountTypeValidation.required('Select account type'),
        account_number: accountNumberValidation.required('Enter account number'),
        routing_number: routingNumberValidation.required('Enter routing number')
    });
    const easySchema = create$3({
        bank_name: bankNameValidation.nullable(),
        nickname: nicknameValidation.nullable(),
        account_owner_name: identityNameValidation.nullable(),
        account_type: accountTypeValidation.nullable(),
        account_number: accountNumberValidation.nullable(),
        routing_number: routingNumberValidation.nullable()
    });
    return allowOptionalFields ? easySchema : schema;
};

class BankAccount {
    constructor(data) {
        this.id = data.id;
        this.account_owner_name = data.account_owner_name;
        this.full_name = data.full_name;
        this.account_type = data.account_type;
        this.acct_last_four = data.acct_last_four;
        this.account_number_last4 = data.account_number_last4;
        this.routing_number = data.routing_number;
        this.bank_name = data.bank_name;
        this.country = data.country;
        this.currency = data.currency;
        this.nickname = data.nickname;
        this.metadata = data.metadata;
        this.business_id = data.business_id;
        this.platform_account_id = data.platform_account_id;
        this.created_at = data.created_at;
        this.updated_at = data.updated_at;
    }
}

const BusinessBankAccountFormStep = /*@__PURE__*/ proxyCustomElement(class BusinessBankAccountFormStep extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.submitted = createEvent(this, "submitted", 7);
        this.stepCompleted = createEvent(this, "form-step-completed", 7);
        this.formLoading = createEvent(this, "formLoading", 7);
        this.errorEvent = createEvent(this, "error-event", 7);
        this.fetchData = async () => {
            this.formLoading.emit(true);
            try {
                const response = await this.api.get(this.businessEndpoint);
                if (response.data.bank_accounts.length > 0) {
                    this.bankAccount = Object.assign({}, new BankAccount(response.data.bank_accounts[0]));
                }
                else {
                    this.bankAccount = Object.assign({}, new BankAccount({}));
                }
                this.formController.setInitialValues(Object.assign({}, this.bankAccount));
            }
            catch (error) {
                this.errorEvent.emit({
                    errorCode: ComponentErrorCodes.FETCH_ERROR,
                    message: error.message,
                    severity: ComponentErrorSeverity.ERROR,
                    data: error,
                });
            }
            finally {
                this.formLoading.emit(false);
            }
        };
        this.sendData = async (onSuccess) => {
            this.formLoading.emit(true);
            try {
                const formValues = this.formController.values.getValue();
                const payload = Object.assign(Object.assign({}, formValues), { business_id: this.businessId });
                const response = await this.api.post(this.bankAccountEndpoint, payload);
                this.handleResponse(response, onSuccess);
            }
            catch (error) {
                this.errorEvent.emit({
                    errorCode: ComponentErrorCodes.POST_ERROR,
                    message: error.message,
                    severity: ComponentErrorSeverity.ERROR,
                    data: error,
                });
            }
            finally {
                this.formLoading.emit(false);
            }
        };
        this.inputHandler = (name, value) => {
            this.formController.setValues(Object.assign(Object.assign({}, this.formController.values.getValue()), { [name]: value }));
        };
        this.formController = undefined;
        this.errors = {};
        this.bankAccount = undefined;
        this.authToken = undefined;
        this.businessId = undefined;
        this.allowOptionalFields = undefined;
    }
    get businessEndpoint() {
        return `entities/business/${this.businessId}`;
    }
    get bankAccountEndpoint() {
        return `entities/bank_accounts`;
    }
    get formDisabled() {
        var _a;
        return !!((_a = this.bankAccount) === null || _a === void 0 ? void 0 : _a.id);
    }
    handleResponse(response, onSuccess) {
        if (response.error) {
            this.errorEvent.emit({
                errorCode: ComponentErrorCodes.POST_ERROR,
                message: response.error.message,
                severity: ComponentErrorSeverity.ERROR,
                data: response.error,
            });
        }
        else {
            onSuccess();
        }
        this.submitted.emit({ data: response, metadata: { completedStep: BusinessFormStep.bankAccount } });
        this.stepCompleted.emit({ data: response, formStep: BusinessFormStepV2.bankAccount });
    }
    async validateAndSubmit({ onSuccess }) {
        if (this.formDisabled) {
            this.stepCompleted.emit({ data: null, formStep: BusinessFormStepV2.bankAccount, metadata: 'no data submitted' });
            onSuccess();
        }
        else {
            this.formController.validateAndSubmit(() => this.sendData(onSuccess));
        }
    }
    ;
    componentWillLoad() {
        this.formController = new FormController(businessBankAccountSchema(this.allowOptionalFields));
        this.api = Api({ authToken: this.authToken, apiOrigin: config.proxyApiOrigin });
        if (this.businessId && this.authToken) {
            this.fetchData();
        }
    }
    componentDidLoad() {
        this.formController.values.subscribe(values => this.bankAccount = Object.assign({}, values));
        this.formController.errors.subscribe(errors => {
            this.errors = Object.assign({}, errors);
        });
    }
    render() {
        const bankAccountDefaultValue = this.formController.getInitialValues();
        return (h("form", { key: '7ae39afd400c9bc2a86f75aeb2a529a36ecd849c' }, h("fieldset", { key: 'd01072ed3710acfbb7916de8ffb9fb80770750af' }, h("div", { key: '75228827d492fcdbd603963a3574c1dca84530b4', class: "d-flex align-items-center gap-2" }, h("legend", { key: '23600460d0b1486d3d4cd612bc9532ba30d5da0e', class: "mb-0" }, "Bank Account Info"), h("form-control-tooltip", { key: 'fed3ad0eef074985a25488a9c093b9eb0cbafabb', helpText: "The Direct Deposit Account is the business bank account where your funds will be deposited. The name of this account must match the registered business name exactly. We are not able to accept personal accounts unless your business is a registered sole proprietorship." })), h("hr", { key: 'f7405cf23ee0a90d46f2c394d09ae421fe9472ee', class: "mt-2" }), h("div", { key: '4cc1d09daed6a897778440f69bd0a9dbd99c1270', class: "row gy-3" }, h("div", { key: 'f7e0e160adaf0925b4ea5ada12137dcf3e5c5d35', class: "col-12" }, h("form-control-text", { key: 'd7cbdb427b9bf332dd9da5885ccfc73d0dd4b40b', name: "bank_name", label: "Bank Name", defaultValue: bankAccountDefaultValue.bank_name, errorText: this.errors.bank_name, inputHandler: this.inputHandler, disabled: this.formDisabled })), h("div", { key: '010c5dd53d8d31f9edc4f0e7ca225c0d2c8a1e09', class: "col-12" }, h("form-control-text", { key: 'f524266d5fcd4e3e35539c130f6dfe742d620593', name: "nickname", label: "Nickname", defaultValue: bankAccountDefaultValue.nickname, errorText: this.errors.nickname, inputHandler: this.inputHandler, disabled: this.formDisabled })), h("div", { key: '1875fc9bb5e0741e1c2aff205effa030aafcd0e2', class: "col-12" }, h("form-control-text", { key: '343754af2350ca668f39270513fa44be416834ba', name: "account_owner_name", label: "Account Owner Name", defaultValue: bankAccountDefaultValue.account_owner_name, errorText: this.errors.account_owner_name, inputHandler: this.inputHandler, disabled: this.formDisabled })), h("div", { key: '9870781e41bd5879ec161569a7b60ecc049690cc', class: "col-12" }, h("form-control-select", { key: '1ade0e3e0f470e3165d227b67c9dd4054d8443fc', name: "account_type", label: "Account Type", options: bankAccountTypeOptions, defaultValue: bankAccountDefaultValue.account_type, errorText: this.errors.account_type, inputHandler: this.inputHandler, disabled: this.formDisabled })), h("div", { key: 'c5e531aaded367bd825913cb100dd3225d1c6f61', class: "col-12" }, h("form-control-text", { key: '39cd3bca5b86a0ca175c26be3982bc0e98953439', name: "account_number", label: "Account Number", defaultValue: bankAccountDefaultValue.account_number, maxLength: 17, errorText: this.errors.account_number, inputHandler: this.inputHandler, keyDownHandler: numberOnlyHandler, disabled: this.formDisabled, helpText: "Please copy the account number as shown on your statement/check. Do not include spaces or dashes." })), h("div", { key: '8c3306dde7149b49d1ad6ba426634674e318b4fd', class: "col-12" }, h("form-control-text", { key: '970305b04e9517f56bcb8217cd938b8971ca34b4', name: "routing_number", label: "Routing Number", defaultValue: bankAccountDefaultValue.routing_number, maxLength: 9, errorText: this.errors.routing_number, inputHandler: this.inputHandler, keyDownHandler: numberOnlyHandler, disabled: this.formDisabled, helpText: "A valid routing number is nine digits. Please include any leading or trailing zeroes." }))))));
    }
}, [0, "justifi-business-bank-account-form-step", {
        "authToken": [1, "auth-token"],
        "businessId": [1, "business-id"],
        "allowOptionalFields": [4, "allow-optional-fields"],
        "formController": [32],
        "errors": [32],
        "bankAccount": [32],
        "validateAndSubmit": [64]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["justifi-business-bank-account-form-step", "custom-popper", "form-control-error-text", "form-control-select", "form-control-text", "form-control-tooltip"];
    components.forEach(tagName => { switch (tagName) {
        case "justifi-business-bank-account-form-step":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, BusinessBankAccountFormStep);
            }
            break;
        case "custom-popper":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "form-control-error-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "form-control-select":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "form-control-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "form-control-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { BusinessBankAccountFormStep as B, defineCustomElement as d };
